import React from 'react';
import {observer} from 'mobx-react-lite';
import {Button, CircularProgress} from '@material-ui/core';
import {AddressVerificationResponse} from '../../APIs/types';
import {VerificationState} from '../../Core/mobx/AddressVerificationStore/AddressVerificationStore';

const FIELD_LABELS: Record<string, string> = {
    streetNumber: 'Street Number',
    street: 'Street',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    unit: 'Unit',
};

const ISSUE_DESCRIPTIONS: Record<string, string> = {
    unconfirmed: 'could not be confirmed',
    suspicious: 'looks suspicious — please double-check',
    missing: 'is missing',
};

type AddressVerificationResultProps = {
    state: VerificationState;
    canSubmit: boolean;
    onAcceptStandardized: () => void;
};

const boxBase: React.CSSProperties = {
    borderRadius: 12,
    padding: '16px 20px',
    marginTop: 12,
};

const titleStyle: React.CSSProperties = {
    fontFamily: 'FredokaMedium',
    fontWeight: 500,
    fontSize: 16,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
};

const bodyStyle: React.CSSProperties = {
    fontFamily: 'FredokaRegular',
    fontSize: 14,
    lineHeight: '22px',
};

const errorBox: React.CSSProperties = {
    ...boxBase,
    backgroundColor: '#fdf0ee',
    border: '1px solid #e8c4bf',
};

const infoBox: React.CSSProperties = {
    ...boxBase,
    backgroundColor: '#eef6f5',
    border: '1px solid #c4dbd8',
};

const successBox: React.CSSProperties = {
    ...boxBase,
    backgroundColor: '#edfbf2',
    border: '1px solid #b8e8c9',
};

const warningBox: React.CSSProperties = {
    ...boxBase,
    backgroundColor: '#fef8ec',
    border: '1px solid #e8dbb8',
};

const pillBtn: React.CSSProperties = {
    fontFamily: 'FredokaRegular',
    fontSize: 14,
    letterSpacing: '0.03em',
    color: '#174943',
    backgroundColor: '#5ae288',
    borderRadius: 102,
    padding: '6px 22px',
    border: '2px solid #5ae288',
    textTransform: 'none' as const,
    marginTop: 12,
    boxShadow: 'none',
};

function Dot({color}: {color: string}) {
    return (
        <span
            style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                flexShrink: 0,
            }}
        />
    );
}

function addressesMatch(
    input: {street: string; city: string; state: string; zip: string; unit?: string} | null,
    standardized: AddressVerificationResponse['standardizedAddress'],
): boolean {
    if (!input) return false;
    return (
        input.street.trim() === standardized.street.trim() &&
        input.city.trim() === standardized.city.trim() &&
        input.state.trim() === standardized.state.trim() &&
        input.zip.trim() === standardized.zip.trim() &&
        (input.unit || '').trim() === (standardized.unit || '').trim()
    );
}

const srOnly: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
};

function FieldDiff({label, original, suggested}: {label: string; original: string; suggested: string}) {
    const changed = original.trim() !== suggested.trim();
    return (
        <div style={{display: 'flex', gap: 12, ...bodyStyle, padding: '2px 0'}}>
            <span style={{width: 56, color: '#5D807B', fontFamily: 'FredokaMedium', fontWeight: 500, flexShrink: 0}}>
                {label}:
            </span>
            {changed ? (
                <>
                    <span style={{textDecoration: 'line-through', color: '#a0a0a0'}}>
                        <span style={srOnly}>Current: </span>
                        {original}
                    </span>
                    <span style={{color: '#456d69', fontFamily: 'FredokaMedium', fontWeight: 500}}>
                        <span style={srOnly}>Suggested: </span>
                        {suggested}
                    </span>
                </>
            ) : (
                <span style={{color: '#434343'}}>{original}</span>
            )}
        </div>
    );
}

function SuggestedAddressBlock({
    state,
    onAcceptStandardized,
}: {
    state: VerificationState;
    onAcceptStandardized: () => void;
}) {
    if (!state.result?.standardizedAddress) return null;
    return (
        <div style={infoBox}>
            <div style={{...titleStyle, color: '#456d69'}}>
                <Dot color="#456d69" />
                Suggested Address
            </div>
            <div style={{marginTop: 4}}>
                <FieldDiff
                    label="Street"
                    original={state.inputSnapshot?.street || ''}
                    suggested={state.result.standardizedAddress.street}
                />
                <FieldDiff
                    label="City"
                    original={state.inputSnapshot?.city || ''}
                    suggested={state.result.standardizedAddress.city}
                />
                <FieldDiff
                    label="State"
                    original={state.inputSnapshot?.state || ''}
                    suggested={state.result.standardizedAddress.state}
                />
                <FieldDiff
                    label="Zip"
                    original={state.inputSnapshot?.zip || ''}
                    suggested={state.result.standardizedAddress.zip}
                />
                {(state.inputSnapshot?.unit || state.result.standardizedAddress.unit) && (
                    <FieldDiff
                        label="Unit"
                        original={state.inputSnapshot?.unit || ''}
                        suggested={state.result.standardizedAddress.unit || ''}
                    />
                )}
            </div>
            <Button style={pillBtn} onClick={onAcceptStandardized}>
                Use Suggested Address
            </Button>
        </div>
    );
}

const AddressVerificationResult = observer(
    ({state, canSubmit, onAcceptStandardized}: AddressVerificationResultProps) => {
        if (state.isVerifying) {
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 0',
                        fontFamily: 'FredokaRegular',
                        fontSize: 14,
                        color: '#5D807B',
                    }}
                >
                    <CircularProgress size={16} style={{color: '#456d69'}} /> Verifying address...
                </div>
            );
        }

        if (state.error) {
            return (
                <div style={errorBox}>
                    <div style={{...titleStyle, color: '#c0392b'}}>
                        <Dot color="#c0392b" />
                        Verification Error
                    </div>
                    <div style={{...bodyStyle, color: '#7a3a30'}}>Unable to verify address. Please try again.</div>
                </div>
            );
        }

        if (!state.result) return null;

        if (state.isStale) {
            return (
                <div style={warningBox}>
                    <div style={{...titleStyle, color: '#8a6d20'}}>
                        <Dot color="#d4a017" />
                        Address Changed
                    </div>
                    <div style={{...bodyStyle, color: '#6b5a1e'}}>
                        Address has changed since verification — please re-verify.
                    </div>
                </div>
            );
        }

        if (!state.result.valid) {
            const componentIssues = state.result.components.filter((c) => !c.confirmed && c.issue);
            const hasStandardized =
                !!state.result.standardizedAddress &&
                !addressesMatch(state.inputSnapshot, state.result.standardizedAddress);
            return (
                <div>
                    <div style={errorBox}>
                        <div style={{...titleStyle, color: '#c0392b'}}>
                            <Dot color="#c0392b" />
                            Invalid Address
                        </div>
                        <ul
                            style={{
                                ...bodyStyle,
                                color: '#7a3a30',
                                listStyleType: 'none',
                                padding: 0,
                                margin: '4px 0 0',
                            }}
                        >
                            {componentIssues.map((c) => (
                                <li key={c.field} style={{padding: '2px 0', paddingLeft: 16, position: 'relative'}}>
                                    <span style={{position: 'absolute', left: 0, color: '#d4847a'}}>-</span>
                                    {FIELD_LABELS[c.field] || c.field} {ISSUE_DESCRIPTIONS[c.issue!] || c.issue}
                                </li>
                            ))}
                            {state.result.missingComponents.map((c) => (
                                <li key={c} style={{padding: '2px 0', paddingLeft: 16, position: 'relative'}}>
                                    <span style={{position: 'absolute', left: 0, color: '#d4847a'}}>-</span>
                                    {FIELD_LABELS[c] || c} is missing
                                </li>
                            ))}
                        </ul>
                    </div>
                    {hasStandardized && (
                        <SuggestedAddressBlock state={state} onAcceptStandardized={onAcceptStandardized} />
                    )}
                </div>
            );
        }

        const matches = addressesMatch(state.inputSnapshot, state.result.standardizedAddress);

        return (
            <div>
                {matches ? (
                    <div style={successBox}>
                        <div style={{...titleStyle, color: '#2d7a4a'}}>
                            <Dot color="#5ae288" />
                            Address Verified
                        </div>
                    </div>
                ) : (
                    <SuggestedAddressBlock state={state} onAcceptStandardized={onAcceptStandardized} />
                )}
            </div>
        );
    },
);

export default AddressVerificationResult;
